import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { ComponentProps } from 'lib/component-props';
import { useMemo } from 'react';
import { useGetArticlesWithThemeQuery } from '@/store/articles/services/articleAPI';
import ArticleCardsComponent from 'components/Generic/ArticleCardsComponent';
import { usePageData } from 'lib/page-data/PageDataContext';
import { useArticlePagination } from 'hooks/useArticlePagination';
import { ArticleFields } from 'components/Tiles/ArticleCard';
import { ArticleCardData } from 'model/Article';

const EMPTY_ARTICLE_CARDS: ArticleCardData[] = [];

const FilteredArticleCards = ({ rendering, fields }: ComponentProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const themeData = usePageData('theme');
  const themeId = themeData?.id;
  const { currentPage, itemsPerPage, handlePageNumberChange, handlePageSizeChange } =
    useArticlePagination();

  const { data, isFetching } = useGetArticlesWithThemeQuery(
    {
      language: sitecoreContext.language || 'en',
      siteId: sitecoreContext.siteRoot || '',
      themeId: themeId || '',
      pageSize: itemsPerPage,
      pageNumber: currentPage,
    },
    {
      skip: !themeId || !sitecoreContext.siteRoot,
    }
  );

  const articles = data?.articles || EMPTY_ARTICLE_CARDS;
  const totalItems = data?.total || 0;

  const mappedArticleData: ArticleFields[] = useMemo(() => {
    return articles.map((article) => ({
      articleImage: article.articleImage?.src,
      articlePublicationDate: article.articlePublicationDate,
      articleStrapline: article.articleStrapline,
      articleTitle: article.articleTitle,
      articleType: article.template?.name || '',
      url: `${fields.baseUrl?.value}/${article.name?.toLowerCase()?.replaceAll(' ', '-')}`,
      id: article.id,
      category: article.articleCategory?.targetItem?.name.value || '',
      name: article.name,
      publicationDate: article.articlePublicationDate?.formattedDateValue,
      duration: article.duration,
    }));
  }, [articles, fields.baseUrl.value]);

  return (
    <ArticleCardsComponent
      articleCards={mappedArticleData}
      totalItems={totalItems}
      pageNumber={currentPage}
      pageSize={itemsPerPage}
      onPageNumberChange={handlePageNumberChange}
      onPageSizeChange={handlePageSizeChange}
      rendering={rendering}
      isLoading={isFetching}
    />
  );
};

export default FilteredArticleCards;
